import React, { FC } from 'react';

import { CheckOutlined, DeleteTwoTone, DownOutlined, ExclamationCircleOutlined, UpOutlined } from '@ant-design/icons';
import { theme as antTheme, Button, message, Modal, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { InfiniteScroll } from '@app/components/InfiniteScroll';
import { NSKeys } from '@app/i18n';
import { RetrieveUserGroupSchema } from '@app/models/users';
import { useWindowSize } from '@app/services/hooks/useWindowSize';

import { useDeleteGroupQuery, useGetGroupsQuery } from '../../hooks';
import { useGroups } from '../../providers';
import { NEW_GROUP_ID } from '../../providers/constants';

import { CancelAction } from './components/CancelAction';
import { EditAction } from './components/EditAction';
import { EditableCell } from './components/EditableCell';
import { EditableRow } from './components/EditableRow';
import { SaveAction } from './components/SaveAction';
import { UserList } from './components/UserList';

import { Container } from './styles';

interface IProps {
  selectable?: boolean;
  searchString?: string;
  disabledRows?: string[];
}

export const GroupTable: FC<IProps> = (props) => {
  const { data, hasNextPage, fetchNextPage, status } = useGetGroupsQuery({
    size: 10,
    name: props.searchString || '',
  });
  const { innerHeight } = useWindowSize();
  const { token } = antTheme.useToken();
  const [modal, contextHolder] = Modal.useModal();
  const commonT = useTranslation(NSKeys.common);
  const { columns, editingRowKey, expandedRowKeys, setExpandedRowKeys, setSelectedRows, newGroups } = useGroups();
  const groupsT = useTranslation(NSKeys.groups);

  const dataSource = React.useMemo(() => {
    return data?.pages?.map((data) => data.items || []).flat() || [];
  }, [data]);

  const { mutate, status: deleteStatus, failureReason } = useDeleteGroupQuery();

  const handleDelete = (data: RetrieveUserGroupSchema) => {
    modal.confirm({
      title: groupsT.t('deleteModal.title'),
      centered: true,
      icon: <ExclamationCircleOutlined />,
      onOk: () => {
        mutate({ user_group_uid: data.uid });
        Modal.destroyAll();
      },
      content: groupsT.t('deleteModal.content', { name: data.name }),
      okText: commonT.t('buttonOk'),
      okButtonProps: { disabled: deleteStatus === 'loading' },
      cancelText: commonT.t('cancel'),
    });
  };

  React.useEffect(() => {
    if (deleteStatus === 'success' || deleteStatus === 'error') {
      Modal.destroyAll();
    }
    if (failureReason && deleteStatus === 'error') {
      const error = failureReason as any;
      const chanis = error?.response?.data?.errors?.[0]?.meta?.order_approval_chains as any[];
      const chainNames = chanis.map((el) => el?.name).join(', ');
      message.open({
        type: 'error',
        duration: 5,
        onClick: () => message.destroy(),
        content: chainNames ? `${groupsT.t('errorDeleteExistInChains')} ${chainNames}` : commonT.t('error400'),
      });
    }
  }, [deleteStatus, failureReason]);

  return (
    <Container>
      {contextHolder}
      <Table<RetrieveUserGroupSchema>
        components={{
          body: {
            row: EditableRow,
            cell: EditableCell,
          },
        }}
        loading={status === 'loading'}
        dataSource={[...newGroups, ...dataSource]}
        rowKey="uid"
        size="large"
        rowClassName="editable-row"
        columns={[
          ...columns.map((el) => ({ ...el, title: groupsT.t(el.title as any) })),
          {
            fixed: 'right',
            key: 'action',
            align: 'center',
            width: 160,
            render: (_, cell) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 8,
                    whiteSpace: 'nowrap',
                  }}>
                  <EditAction record={cell} />
                  <CancelAction record={cell} />
                  <SaveAction record={cell} />
                  {cell.uid !== NEW_GROUP_ID && cell?.$permissions.delete && !editingRowKey ? (
                    <Tooltip title={commonT.t('buttonDelete')}>
                      <Button
                        type="link"
                        icon={<DeleteTwoTone twoToneColor={token?.colorPrimary} />}
                        onClick={() => handleDelete(cell)}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              );
            },
          },
        ]}
        onRow={((record: RetrieveUserGroupSchema) => ({ record })) as any}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => <UserList record={record} />,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, { uid }) => {
            setExpandedRowKeys(expanded ? [uid] : []);
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            if (!record?.$permissions?.view) {
              return null;
            }
            if (expanded) {
              return (
                <UpOutlined
                  style={{ width: '10px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand(record, e);
                  }}
                />
              );
            }
            return (
              <DownOutlined
                style={{ width: '10px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand(record, e);
                }}
              />
            );
          },
        }}
        {...(props.selectable && {
          rowSelection: {
            getCheckboxProps: (record) => ({
              disabled: props.disabledRows?.some((el) => el === record.uid),
            }),
            type: 'radio',
            onChange: (key) => {
              setSelectedRows(key);
            },
          },
        })}
        scroll={{ y: innerHeight - 350 }}
        style={{ overflow: 'auto' }}
      />
      {hasNextPage && <InfiniteScroll key={dataSource.length || 0} next={fetchNextPage} />}
    </Container>
  );
};
